'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useUser } from '@clerk/nextjs';
import { useManagedWebSocket } from '@/lib/hooks/use-managed-websocket';
import { getToken } from '@/app/actions/auth';
import useWebSocketStore from '@/lib/hooks/zustand-stores/use-websocket-store';
import { useLogger } from '@/lib/hooks/useLogger';
import useWebSocketReconnect from '@/lib/hooks/use-websocket-reconnect';
import { toast } from 'sonner';

// Create a global variable to ensure we only initialize WebSocket once
let hasInitializedWebSocket = false;

/**
 * WebSocketProvider - Manages WebSocket connections for the application
 * 
 * This component handles:
 * 1. Establishing WebSocket connections when a user is authenticated
 * 2. Disconnecting when a user logs out
 * 3. Reconnecting when authentication changes
 * 4. Token management for WebSocket authentication
 * 5. Reconnecting when user returns to tab (visibility change)
 */
export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user, isSignedIn } = useUser();
  const [userToken, setUserToken] = useState<string | null>(null);
  const [isTokenLoading, setIsTokenLoading] = useState(false);
  const [tokenFetchError, setTokenFetchError] = useState<string | null>(null);
  const logger = useLogger();
  
  // Get WebSocket store functions
  const setAuthToken = useWebSocketStore(state => state.setAuthToken);
  const connectionStatus = useWebSocketStore(state => state.connectionStatus);
  
  // Token fetch attempts tracking
  const tokenFetchAttempts = useRef(0);
  const lastTokenFetchTime = useRef(0);
  const MAX_TOKEN_FETCH_ATTEMPTS = 5;
  const TOKEN_FETCH_COOLDOWN = 30000; // 30 seconds
  
  // Add ref for tracking previous state outside of useEffect
  const prevStateRef = useRef(JSON.stringify({}));
  
  // WebSocket Connection - Only when token is ready
  const { connectionState } = useManagedWebSocket({
    // Only connect when token is fetched successfully and user is signed in
    autoConnect: !!isSignedIn && !!user?.id && !!userToken,
    userId: user?.id || undefined,
    token: userToken || undefined,
    onConnect: () => {
      logger.debug('WebSocketProvider: WebSocket connected successfully');
    },
    onDisconnect: () => {
      logger.debug('WebSocketProvider: WebSocket disconnected');
    },
    onError: (error) => {
      logger.error('WebSocketProvider: WebSocket connection error:', error);
    }
  });
  
  // Use our custom reconnection hook to handle visibility changes
  useWebSocketReconnect({
    // Only enable reconnect if we have a valid user and token
    enabled: !!isSignedIn && !!user?.id && !!userToken,
    onReconnect: () => {
      logger.debug('WebSocketProvider: Attempting reconnection after tab visibility change');
    },
    onReconnectSuccess: () => {
      logger.debug('WebSocketProvider: Successfully reconnected after tab visibility change');
      // Consider showing a toast for a better user experience
      // toast.success('Reconnected to real-time updates');
    },
    onReconnectFail: (error) => {
      logger.error('WebSocketProvider: Failed to reconnect after tab visibility change:', error);
      // Consider showing an error toast for a better user experience
      // toast.error('Failed to reconnect to real-time updates. Please refresh the page.');
    }
  });
  
  // Token Fetch Logic - Only runs once per user session
  useEffect(() => {
    // Skip if already initialized globally
    if (hasInitializedWebSocket) {
      logger.debug('WebSocketProvider already initialized globally - skipping');
      return;
    }
    
    // Skip if no user is signed in
    if (!isSignedIn || !user?.id) {
      logger.debug('WebSocketProvider: No user signed in, skipping token fetch');
      setAuthToken(null);
      setUserToken(null);
      return;
    }
    
    // Skip if already loading token
    if (isTokenLoading) {
      logger.debug('WebSocketProvider: Token already being fetched, skipping');
      return;
    }
    
    // Check if we've hit the rate limit
    if (tokenFetchAttempts.current >= MAX_TOKEN_FETCH_ATTEMPTS) {
      const now = Date.now();
      const timeSinceLastFetch = now - lastTokenFetchTime.current;
      
      if (timeSinceLastFetch < TOKEN_FETCH_COOLDOWN) {
        const remainingCooldown = Math.ceil((TOKEN_FETCH_COOLDOWN - timeSinceLastFetch) / 1000);
        logger.warn(`WebSocketProvider: Too many token fetch attempts. Cooling down for ${remainingCooldown}s`);
        setTokenFetchError(`Too many token fetch attempts. Trying again in ${remainingCooldown} seconds.`);
        return;
      }
      
      // Reset counter after cooldown
      tokenFetchAttempts.current = 0;
    }
    
    const fetchToken = async () => {
      setIsTokenLoading(true);
      tokenFetchAttempts.current += 1;
      lastTokenFetchTime.current = Date.now();
      
      try {
        logger.debug(`WebSocketProvider: Fetching token for WebSocket (attempt ${tokenFetchAttempts.current})`);
        const token = await getToken();
        
        if (token) {
          logger.debug('WebSocketProvider: Successfully fetched token');
          setAuthToken(token);
          setUserToken(token);
          setTokenFetchError(null);
          
          // Reset attempt counter on success
          tokenFetchAttempts.current = 0;
          
          // Mark as globally initialized
          hasInitializedWebSocket = true;
        } else {
          const errorMsg = 'No token returned from getToken()';
          logger.error(`WebSocketProvider: ${errorMsg}`);
          setTokenFetchError(errorMsg);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('WebSocketProvider: Failed to get token:', errorMessage);
        setTokenFetchError(errorMessage);
      } finally {
        setIsTokenLoading(false);
      }
    };
    
    fetchToken();
  }, [isSignedIn, user?.id, setAuthToken, isTokenLoading, logger]);
  
  // Status logging
  useEffect(() => {
    // Only log status changes when they actually change to reduce noise
    const state = { 
      isSignedIn, 
      hasUser: !!user, 
      userId: user?.id,
      connectionStatus,
      connectionState,
      hasToken: !!userToken,
      isTokenLoading,
      tokenFetchAttempts: tokenFetchAttempts.current,
      hasError: !!tokenFetchError,
      hasInitializedGlobally: hasInitializedWebSocket
    };
    
    // Store the state in a ref so we can compare it
    const currentState = JSON.stringify(state);
    
    // Only log if the state has changed
    if (prevStateRef.current !== currentState) {
      logger.debug('WebSocketProvider state:', state);
      prevStateRef.current = currentState;
    }
  }, [isSignedIn, user, connectionStatus, connectionState, userToken, isTokenLoading, tokenFetchError, logger]);
  
  return <>{children}</>;
}

export default WebSocketProvider; 